import {
  createContext,
  useContext,
  useCallback,
  useEffect,
  useState,
} from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { logUserToMixpanel } from "../utils/mixpanel";
import { useAPI } from "../hooks/useAPI";
import { checkConnectionQuality } from "../services/connectionTest";

const AuthContext = createContext(null);

export const AuthProvider = ({ children, enabled = true }) => {
  const queryClient = useQueryClient();
  const { callAPI } = useAPI();

  // 1. Token is held in memory ONLY. The durable session lives in an HttpOnly
  // cookie the browser attaches automatically (credentials: "include"), so it is
  // not readable by JS. This in-memory value is lost on refresh by design; auth
  // state is derived from the /user query below, not from this token.
  const [authToken, setAuthToken] = useState("");

  // Helper to determine 2FA status
  const is2FA =
    typeof window !== "undefined" &&
    window.location.pathname.toLowerCase().includes("2fa");

  // 2. Define the Query for Fetching User
  const {
    data: loggedInUser = {},
    isLoading,
    error,
    refetch: fetchUser,
  } = useQuery({
    queryKey: ["user"],
    queryFn: async () => {
      // Authenticated by the HttpOnly cookie (credentials: "include"); the
      // in-memory token, when present, is sent as a header fallback.
      const userData = await callAPI(`user`, { token: authToken });

      if (!Object.keys(userData).includes("email")) {
        throw new Error("Invalid user");
      }
      return userData;
    },
    // Runs whenever we're not mid-2FA and the provider is enabled. A 401 (logged
    // out) surfaces as an error → isAuthenticated false.
    enabled: !is2FA && enabled,
    retry: false, // Don't retry if auth fails (usually means 401)
    staleTime: 1000 * 60 * 5, // Cache user data for 5 minutes
  });

  // 3. Side Effect: Logging to Mixpanel
  useEffect(() => {
    if (loggedInUser && loggedInUser.email) {
      logUserToMixpanel(loggedInUser);
    }
  }, [loggedInUser]);

  // After a refresh the in-memory token is gone but the HttpOnly cookie may
  // still be valid. Derive the exposed auth token DURING RENDER (not in an
  // effect) so it's truthy the same render /user resolves — otherwise gated
  // child queries would mount one render with an empty token (disabled query,
  // isLoading=false) and render with un-fetched data. The value is only a header
  // fallback; the cookie is the real credential, so the sentinel is fine.
  const effectiveAuthToken =
    authToken || (loggedInUser?.email ? "cookie-session" : "");

  // 4. Connection quality check — runs once when the user is authenticated.
  // Skipped on auth-flow pages (/2fa_*, /login) because saveAuthToken fires the
  // authToken change there, and the test's in-flight /test_connection would be
  // aborted by the imminent window.location.href reload to /dashboard/home.
  useEffect(() => {
    if (!authToken) return;
    const path = window.location.pathname.toLowerCase();
    if (path.includes("2fa") || path.startsWith("/login")) return;
    checkConnectionQuality(callAPI, authToken).catch(() => {});
  }, [authToken]);

  // 5. Save Token Helper. The backend has already set the HttpOnly auth cookie on
  // the login/2FA response; we just hold the token in memory for this session's
  // header fallback and refetch the user now that we're authenticated.
  const saveAuthToken = useCallback(
    (token) => {
      setAuthToken(token || "");
      queryClient.invalidateQueries({ queryKey: ["user"] });
    },
    [queryClient]
  );

  // Logout: ask the backend to delete the token and expire the HttpOnly cookie,
  // then clear all client state. Fire-and-forget — clear regardless of result.
  const logout = useCallback(() => {
    callAPI("logout", { method: "POST" }).catch(() => {});
    setAuthToken("");
    queryClient.clear();
    // Remove the persisted React Query cache so stale data isn't restored on
    // the next login (key is set in main.jsx PersistQueryClientProvider).
    localStorage.removeItem("GURUNOW_REACT_QUERY_CACHE");
    localStorage.removeItem("gn_conn_test");
    sessionStorage.clear();
  }, [queryClient, callAPI]);



  const value = {
    authToken: effectiveAuthToken,
    saveAuthToken,
    loggedInUser,
    fetchUser,
    isLoading,
    error,
    logout,
    isAuthenticated: !!loggedInUser?.email,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Custom Hook to consume the context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
